/**
 * Admin V3 — Bookings module — Table component
 *
 * 9 columns (SC-PR-7 added "Tier" between Prix and Chauffeur; was 8 cols
 * in M2A): Référence | Date | Trajet | Client | Prix | Tier | Chauffeur |
 * Statut | Actions.
 *
 * Per-row left-border colour (status-tinted) verbatim from AdminDashboard.js:1109.
 * Reference cell + Ouvrir icon both navigate to /admin/bookings/:id (legacy V2
 * detail page with A1+A2 refund — Phase 2.2 decision #1, M2A precedent).
 *
 * NO mutation surface. NO assign/unassign/auto-assign controls (Phase 3).
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
// SC-PR-11: Scale icon for the new "Refund Guidance" advisory-only
// action button. Read-only consumer of GET /admin/bookings/{id}/refund-recommendation.
import { History, ExternalLink, Calendar, AlertCircle, RotateCcw, Scale, CalendarClock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn, currency, shortAddress, StatusPill, TierBadge, EmptyState,
} from '../../shared/visualTokens';
import usePermissions from '../../shared/hooks/usePermissions';
import bookingDetailPath from './bookingDetailPath';
import { t } from '../../i18n';

// Dark-launch: the "Planifier" action (commercial shell → confirmed) is gated
// by a build-time flag (default OFF), mirroring the project's dark discipline.
const SCHEDULING_ENABLED = import.meta.env.VITE_COMMERCIAL_SCHEDULING === 'true';

// Verbatim from AdminDashboard.js:1109 — left-edge row border colour by status.
// Only the four statuses below get an explicit colour in legacy; others get transparent.
const ROW_LEFT_BORDER_HEX = {
  pending:   '#f59e0b',
  confirmed: '#3b82f6',
  completed: '#22c55e',
  cancelled: '#ef4444',
};

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(8).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const BookingsListTable = ({ bookings, loading, error, isFilterActive, onOpenTimeline, onOpenRefundGuidance, onSchedule, onRetry, locale = 'fr' }) => {
  const navigate = useNavigate();
  // SC-PR-11: gate the new "Refund Guidance" action button on the
  // backend-driven permission. HIDE posture: button hidden unless the
  // operator's resolved permission set includes refund_policy:read.
  const { can } = usePermissions();
  const canSeeRefundGuidance = can('refund_policy:read');
  // P0-1b: gate the schedule transition (awaiting_schedule → confirmed) on the
  // backend permission ADMIN_BOOKINGS_SCHEDULE ("admin:bookings:schedule",
  // POST /admin/bookings/{id}/schedule). Fail-closed HIDE — can() is false when
  // the permission set is unresolved. Backend @rbac_permission stays authoritative;
  // this permission gate is additive to the existing SCHEDULING_ENABLED flag.
  const canSchedule = can('admin:bookings:schedule');

  // Top-level error banner (separate from in-table empty state)
  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-bookings-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('bookings.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-bookings-retry"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('bookings.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={Calendar}
      title={t('bookings.empty.title', locale)}
      desc={t(isFilterActive ? 'bookings.empty.descFilter' : 'bookings.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {[
                t('bookings.col.reference', locale),
                t('bookings.col.date', locale),
                t('bookings.col.route', locale),
                t('bookings.col.client', locale),
                t('bookings.col.price', locale),
                // SC-PR-7: new tier column between Prix and Chauffeur so
                // the operator can see at a glance which widget tier the
                // customer chose (ECO / BUSINESS / VAN; G3.E removed PRIORITY).
                t('bookings.col.tier', locale),
                t('bookings.col.driver', locale),
                t('bookings.col.status', locale),
                t('bookings.col.actions', locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : bookings.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              bookings.map((booking) => {
                const leftColor = ROW_LEFT_BORDER_HEX[booking.status] || 'transparent';
                const refLabel = booking.booking_reference || booking.id?.substring(0, 8).toUpperCase() || '—';
                return (
                  <tr key={booking.id} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                    <td className="px-4 py-3.5" style={{ boxShadow: `inset 3px 0 0 ${leftColor}` }}>
                      <Link
                        to={bookingDetailPath(booking.id)}
                        className="inline-block rounded-lg bg-blue-50 px-2 py-1 font-mono text-[12px] font-bold text-blue-700 hover:bg-blue-100"
                        data-testid={`adminv3-booking-ref-${booking.id}`}
                      >
                        {refLabel}
                      </Link>
                    </td>
                    <td className="px-4 py-3.5 text-slate-700">
                      {booking.date || '—'}
                      <div className="text-[11px] text-slate-400">{booking.time || '—'}</div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="font-medium text-slate-900">{shortAddress(booking.pickup_address, 24)}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        <span className="text-amber-500 mr-1">→</span>
                        {shortAddress(booking.destination_address, 24)}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-slate-700">
                      {booking.email || '—'}
                      <div className="text-[11px] text-slate-400">{booking.phone || '—'}</div>
                    </td>
                    <td className="px-4 py-3.5 font-bold text-slate-900">
                      {booking.final_price != null ? currency(booking.final_price)
                        : booking.estimated_price != null ? currency(booking.estimated_price)
                        : currency(0)}
                    </td>
                    {/*
                      SC-PR-7: tier badge cell. Reads booking.service_type
                      from the admin list endpoint (admin.py:GET /admin/bookings)
                      which since SC-PR-7 returns the field on every row.
                      Null / "standard" / unknown render as em-dash via TierBadge.
                    */}
                    <td className="px-4 py-3.5" data-testid={`adminv3-booking-tier-${booking.id}`}>
                      <TierBadge tier={booking.service_type} />
                    </td>
                    <td className="px-4 py-3.5">
                      {booking.assigned_driver_name ? (
                        <div className="flex items-center gap-2">
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-amber-400">
                            {booking.assigned_driver_name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <span className="text-slate-700">{booking.assigned_driver_name}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">{t('bookings.driver.none', locale)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusPill status={booking.status} />
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          onClick={() => onOpenTimeline(booking)}
                          className={cn(
                            'rounded-lg p-1.5 hover:bg-slate-100 hover:text-slate-700 transition-colors',
                            booking.status_timeline?.length > 0 ? 'text-blue-400' : 'text-slate-300'
                          )}
                          title={t('bookings.action.timeline', locale)}
                          data-testid={`adminv3-booking-timeline-${booking.id}`}
                        >
                          <History className="h-4 w-4" />
                        </button>
                        {/*
                          SC-PR-11: new advisory-only action — opens the
                          refund-guidance modal. The modal lazy-fetches
                          GET /admin/bookings/{id}/refund-recommendation
                          and displays the engine's recommendation. It
                          does NOT trigger a refund and does NOT pre-fill
                          the legacy RefundModal amount field. Hidden if
                          operator lacks refund_policy:read permission
                          (HIDE posture mirrors backend RBAC gate).
                        */}
                        {canSeeRefundGuidance && onOpenRefundGuidance ? (
                          <button
                            type="button"
                            onClick={() => onOpenRefundGuidance(booking)}
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                            title={t('bookings.action.refundGuidance', locale)}
                            data-testid={`adminv3-booking-refund-guidance-${booking.id}`}
                          >
                            <Scale className="h-4 w-4" />
                          </button>
                        ) : null}
                        {/* Booking-owned scheduling transition (awaiting_schedule → confirmed).
                            Visible only for commercial shells awaiting scheduling and only when
                            the dark flag is ON. Does NOT assign a driver. */}
                        {SCHEDULING_ENABLED && canSchedule && onSchedule && booking.status === 'awaiting_schedule' ? (
                          <button
                            type="button"
                            onClick={() => onSchedule(booking)}
                            className="rounded-lg p-1.5 text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                            title={t('schedule.action', locale)}
                            data-testid={`adminv3-booking-schedule-${booking.id}`}
                          >
                            <CalendarClock className="h-4 w-4" />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => navigate(bookingDetailPath(booking.id))}
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={t('bookings.action.open', locale)}
                          data-testid={`adminv3-booking-open-${booking.id}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default BookingsListTable;
